import {
  moveToFulfilled,
  moveToPending,
  moveToRejected,
  objectWithoutKeys,
  replaceFulfilled,
  update,
} from '@ms/yammer-data-support';
import { CampaignState } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import { CampaignDeleteFulfilledAction } from '../../domains/campaignDeletion/actions.types';
import { CampaignEditFulfilledAction, CampaignPublishFulfilledAction } from '../../domains/campaignEdit/actions.types';
import { CampaignUnfollowFulfilledAction } from '../../domains/campaignFollowing/unfollowActions.types';
import { CampaignSashAddFulfilledAction } from '../../domains/campaignSash/addSashActions.types';
import { CampaignSashRemoveFulfilledAction } from '../../domains/campaignSash/removeSashActions.types';
import { PublishUserMessagePostTypesFulfilledAction } from '../../domains/publishers/actions.types';
import { UserFollowedByLoadFulfilledAction } from '../../domains/userFollowedBy/actions.types';
import { UserFollowedUsersLoadFulfilledAction } from '../../domains/userFollowedUsers/actions.types';
import { UserHeaderLoadAction, UserHeaderLoadClearedAction } from '../../domains/userHeaderLoad/actions.types';

import { UserHeaders } from './types';

export type UserHeaderAction =
  | CampaignEditFulfilledAction
  | CampaignPublishFulfilledAction
  | CampaignDeleteFulfilledAction
  | CampaignSashAddFulfilledAction
  | CampaignSashRemoveFulfilledAction
  | UserHeaderLoadAction
  | UserHeaderLoadClearedAction
  | UserFollowedByLoadFulfilledAction
  | UserFollowedUsersLoadFulfilledAction
  | PublishUserMessagePostTypesFulfilledAction
  | CampaignUnfollowFulfilledAction;

const initialState: UserHeaders = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const updateTotalPostsCount = (
  state: UserHeaders,
  { userId }: PublishUserMessagePostTypesFulfilledAction['payload']
) => {
  const totalPosts = state.fulfilled?.byId[userId]?.totalPosts;

  if (totalPosts !== undefined) {
    const updatedTotalPosts = totalPosts + 1;

    return update(state, userId, { totalPosts: updatedTotalPosts });
  }

  return state;
};

const updateFollowedUsersTotalCount = (
  state: UserHeaders,
  { id, entities: { followedUsers } }: UserFollowedUsersLoadFulfilledAction['payload']
) => {
  const totalFollowedUsers = followedUsers[id].totalCount;

  return update(state, id, { totalFollowedUsers });
};

const updateFollowedByTotalCount = (
  state: UserHeaders,
  { id, entities: { followedBy } }: UserFollowedByLoadFulfilledAction['payload']
) => {
  const totalFollowers = followedBy[id].totalCount;

  return update(state, id, { totalFollowers });
};

const removeUserHeader = (state: UserHeaders, { id }: UserHeaderLoadClearedAction['payload']) => {
  const newState = objectWithoutKeys(state.fulfilled.byId, [id]);

  return replaceFulfilled(state, newState);
};

const addUserSash = (state: UserHeaders, { userId: id, entities }: CampaignSashAddFulfilledAction['payload']) => {
  const { campaignId, hashtag, isOfficial, color } = entities.userSashes[id];

  return update(state, id, {
    campaignSashTitle: hashtag,
    campaignSashColor: color,
    campaignId,
    campaignIsOfficial: isOfficial,
  });
};

const removeUserSash = (state: UserHeaders, id: string, campaignId: string) => {
  const userHeader = state.fulfilled.byId[id];

  if (userHeader && campaignId === userHeader.campaignId) {
    return update(state, id, {
      campaignSashTitle: undefined,
      campaignSashColor: undefined,
      campaignId: undefined,
      campaignIsOfficial: undefined,
    });
  }

  return state;
};

const removeEndCampaignSash = (
  state: UserHeaders,
  {
    userId,
    id: campaignId,
    entities,
  }: CampaignEditFulfilledAction['payload'] | CampaignPublishFulfilledAction['payload']
) => {
  const campaignReferences = entities.campaignReferences[campaignId];
  if (campaignReferences && campaignReferences.state === CampaignState.CLOSED) {
    return removeUserSash(state, userId, campaignId);
  }

  return state;
};

const reducer: Reducer<UserHeaders, UserHeaderAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'CAMPAIGN.PUBLISH_FULFILLED':
      return removeEndCampaignSash(state, action.payload);
    case 'CAMPAIGN.EDIT_FULFILLED':
      return removeEndCampaignSash(state, action.payload);
    case 'CAMPAIGN.DELETE_FULFILLED':
      return removeUserSash(state, action.payload.userId, action.payload.campaignId);
    case 'CAMPAIGN.SASH.ADD_FULFILLED':
      return addUserSash(state, action.payload);
    case 'CAMPAIGN.SASH.REMOVE_FULFILLED':
      return removeUserSash(state, action.payload.userId, action.payload.campaignId);
    case 'CAMPAIGN.UNFOLLOW_FULFILLED':
      return removeUserSash(state, action.payload.userId, action.payload.id);
    case 'USER_HEADER.LOAD_PENDING':
      return moveToPending(state, [action.payload.id]);
    case 'USER_HEADER.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.id], action.payload.error);
    case 'USER_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.userHeaders);
    case 'USER_HEADER.LOAD_CLEARED':
      return removeUserHeader(state, action.payload);
    case 'USER_FOLLOWED_USERS.LOAD_FULFILLED':
      return updateFollowedUsersTotalCount(state, action.payload);
    case 'USER_FOLLOWED_BY.LOAD_FULFILLED':
      return updateFollowedByTotalCount(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return updateTotalPostsCount(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return updateTotalPostsCount(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return updateTotalPostsCount(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
